import { HTMLMotionProps, m } from 'framer-motion';
import Image, { StaticImageData } from 'next/image';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { BalooBhaifont } from '@/lib/utils';
import { buttonAnimation } from '@/lib/animation';
import BgGray from './assets/bg-shadow-gray.png';
import BgBluePng from './assets/bg-shadow-blue.png';
import BgYellowPng from './assets/bg-shadow-yellow.png';
import BgDarkCerulean from './assets/bg-shadow-dark-cerulean.png';
import BgGrayDisabled from './assets/bg-shadow-gray-disable.png';
import BgViolet from './assets/bg-shadow-violet.png';
import BgGreen from './assets/bg-shadow-green.png';

export enum ButtonColorScheme {
  Gray,
  Blue,
  Yellow,
  DarkCerulean,
  GrayDisable,
  Violet,
  Green,
  MultiTheme,
  MultiThemeDisable,
}

export type ButtonIeltsProps = Omit<HTMLMotionProps<'button'>, 'ref'> & {
  typeButton?: ButtonColorScheme;
  className?: string;
  Icon?: React.FC<JSX.IntrinsicElements['svg']>;
  style?: object;
  animation?: object | boolean | null;
  classNameIcon?: string;
  classNameIconRight?: string;
  icon?: StaticImageData;
  loading?: boolean;
  customTextStyle?: string;
  wrapClassName?: string;
  textStyle?: React.CSSProperties;
  title?: React.ReactNode;
  IconRight?: React.FC<JSX.IntrinsicElements['svg']>;
};

export default forwardRef<HTMLButtonElement, ButtonIeltsProps>(function ButtonIelts(
  {
    title,
    className,
    Icon,
    IconRight,
    typeButton = ButtonColorScheme.Gray,
    style,
    animation,
    classNameIcon,
    classNameIconRight,
    loading,
    customTextStyle,
    wrapClassName,
    textStyle,
    ...props
  },
  ref
) {
  const getAnimate = useCallback(() => {
    if (animation !== undefined) {
      return typeof animation === 'object' ? animation : {};
    }
    if (loading) {
      return {};
    }
    return buttonAnimation;
  }, [animation, loading]);

  const renderStyle = useMemo(() => {
    if (typeButton === ButtonColorScheme.Gray) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#423862] pb-[2px] h-[53px] w-[126px]',
        titleStyle: 'text-white text-[16px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#726695]',
        BgShadow: BgGray,
      };
    }
    if (typeButton === ButtonColorScheme.Blue) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#057ACB] pb-[2px] h-[58px] w-[406.168px]',
        titleStyle: 'text-white text-[26px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#30B8FF]',
        BgShadow: BgBluePng,
      };
    }
    if (typeButton === ButtonColorScheme.Yellow) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#F39E2B] pb-[2px] h-[64px] w-[236px]',
        titleStyle: 'text-white text-[26px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#F8C039]',
        BgShadow: BgYellowPng,
      };
    }
    if (typeButton === ButtonColorScheme.DarkCerulean) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#094A84] pb-[2px] h-[60px] w-[186px]',
        titleStyle: 'text-white text-[26px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#1168AD]',
        BgShadow: BgDarkCerulean,
      };
    }
    if (typeButton === ButtonColorScheme.Violet) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#53496F] pb-[2px] h-[64px] w-[236px]',
        titleStyle: 'text-white text-[26px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#726695]',
        BgShadow: BgViolet,
      };
    }
    if (typeButton === ButtonColorScheme.Green) {
      return {
        buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#006476] pb-[2px] h-[64px] w-[236px]',
        titleStyle: 'text-white text-[26px] font-extrabold [text-shadow:0px_2px_3px_#000]',
        wrapContentStyle: 'bg-[#50D500]',
        BgShadow: BgGreen,
      };
    }
    if (typeButton === ButtonColorScheme.MultiTheme) {
      return {
        buttonContainer: 'border-none h-[64px] w-[236px]',
        titleStyle: 'text-white text-[26px]',
      };
    }
    if (typeButton === ButtonColorScheme.MultiThemeDisable) {
      return {
        buttonContainer: 'border-none h-[64px] w-[236px]',
        titleStyle: 'text-white text-[26px]',
        wrapContentStyle: 'bg-[#B6B6B6]',
      };
    }

    return {
      buttonContainer: 'border-[#0D0C11] border-solid border-[2px] rounded-[9px] bg-[#636363] pb-[2px] h-[64px] w-[236px]',
      titleStyle: 'text-white text-[26px] font-extrabold  [text-shadow:0px_2px_3px_#000]',
      wrapContentStyle: 'bg-[#B6B6B6]',
      BgShadow: BgGrayDisabled,
    };
  }, [typeButton]);

  return (
    <m.button
      ref={ref}
      type="button"
      className={twMerge(
        loading ? 'cursor-not-allowed' : 'cursor-pointer',
        'flex justify-center items-center align-middle outline-none transition-all duration-75 min-w-[126px] min-h-[53px] relative',
        renderStyle.buttonContainer,
        className
      )}
      style={style}
      {...getAnimate()}
      {...props}
      disabled={props.disabled || loading}
    >
      <div
        className={twMerge(
          'relative w-full h-full flex justify-center items-center align-middle rounded-[9px]',
          renderStyle.wrapContentStyle,
          wrapClassName
        )}
      >
        <div
          className={twMerge('w-full text-center z-20 flex justify-center items-center gap-[12px] pt-[3px]', loading ? 'opacity-0' : 'opacity-100')}
        >
          {Icon && (
            <div className={`left-[10px] ${classNameIcon || ''}`}>
              <Icon className="w-[25px] icon-button" />
            </div>
          )}
          {props.icon && (
            <div className={`left-[10px] ${classNameIcon || ''}`}>
              <Image src={props.icon} alt="icon" className="w-[20px] icon-button" />
            </div>
          )}
          <span className={twMerge(renderStyle.titleStyle, BalooBhaifont.className, customTextStyle)} style={textStyle}>
            {title}
          </span>
          {IconRight && (
            <div className={`right-[27px] ${classNameIconRight || ''}`}>
              <IconRight className="w-[25px] icon-button" />
            </div>
          )}
        </div>
        {loading && (
          <div className="absolute z-50 inset-0 flex justify-center items-center bg-[rgba(0,0,0,0.3)] rounded-[inherit]">
            <div className="dot-pulse" />
          </div>
        )}
        {renderStyle.BgShadow && (
          <div className="absolute z-10 w-full h-full">
            <Image alt="flyer" src={renderStyle.BgShadow} className="w-full h-full rounded-[9px]" />
          </div>
        )}
      </div>
    </m.button>
  );
});
